import focusLock from "dom-focus-lock"

type Props = {
  topBarSelector: string
  dropdownToggleSelector: string
  dropdownToggleActiveClass: string
  navItemSelector: string
  dropdownSelector: string
  maskActiveClass: string
  hamburgerToggleSelector: string
  mobileActiveClass: string
  menuSelector: string
  backToMainSelector: string
  subMenuSelector: string
  subMenuActiveClass: string
  subMenuToggleSelector: string
  subMenuDropdownSelector: string
  dropdownCloseButtonSelector: string
  backgroundColor: string
}

export default function masthead(
  el: HTMLElement,
  {
    topBarSelector,
    dropdownToggleSelector,
    dropdownToggleActiveClass,
    navItemSelector,
    dropdownSelector,
    maskActiveClass,
    hamburgerToggleSelector,
    mobileActiveClass,
    menuSelector,
    backToMainSelector,
    subMenuSelector,
    subMenuActiveClass,
    subMenuToggleSelector,
    dropdownCloseButtonSelector,
    backgroundColor,
  }: Props,
) {
  const topBarHeight =
    document.querySelector(topBarSelector)?.getBoundingClientRect().height || 38
  const navItemEls: NodeListOf<HTMLElement> =
    el.querySelectorAll(navItemSelector)
  const dropdownToggleEls: NodeListOf<HTMLElement> = el.querySelectorAll(
    dropdownToggleSelector,
  )
  const hamburgerToggleEl: HTMLElement | null = el.querySelector(
    hamburgerToggleSelector,
  )
  const subMenuToggleEls = el.querySelectorAll(subMenuToggleSelector)
  const menuEl: HTMLElement | null = el.querySelector(menuSelector)
  const bodyEl: HTMLBodyElement | null = document.querySelector("body")
  const subMenuItemEls = el.querySelectorAll(subMenuSelector)
  const dropdownCloseButtonEls: NodeListOf<HTMLElement> = el.querySelectorAll(
    dropdownCloseButtonSelector,
  )

  if (
    !navItemEls ||
    !dropdownToggleEls ||
    !hamburgerToggleEl ||
    !menuEl ||
    !bodyEl ||
    !dropdownCloseButtonEls
  ) {
    return
  }

  let navIsActive = false

  const mediaQuery = window.matchMedia("(min-width: 1200px)")

  mediaQuery.addEventListener("change", checkMedia)

  function checkMedia(e: MediaQueryListEvent) {
    if (
      !dropdownToggleEls ||
      !hamburgerToggleEl ||
      !menuEl ||
      !bodyEl ||
      !dropdownCloseButtonEls
    ) {
      return
    }
    if (e.matches) {
      hideMobileMenu()
    }
  }

  initializeMasthead()

  function initializeMasthead() {
    subMenuToggleEls.forEach(el => {
      el.setAttribute("aria-expanded", "false")
    })
    // Add active class to nav toggles on click
    navItemEls.forEach(navItemEl => {
      const dropdownToggleEl: HTMLElement | null = navItemEl.querySelector(
        dropdownToggleSelector,
      )
      const dropdownEl: HTMLElement | null =
        navItemEl.querySelector(dropdownSelector)
      const backToMainEl: HTMLElement | null =
        navItemEl.querySelector(backToMainSelector)

      if (
        dropdownToggleEl == null ||
        backToMainEl == null ||
        dropdownEl == null
      ) {
        return
      }

      // Mouse clicks dropdown toggle target
      navItemEl.addEventListener("click", function (event) {
        handleToggleNavItemClick(
          navItemEl,
          event,
          dropdownToggleEl,
          backToMainEl,
          dropdownEl,
        )
      })
    })

    // Hamburger (mobile) menu toggle
    if (hamburgerToggleEl) {
      hamburgerToggleEl.addEventListener("click", function () {
        toggleMobileMenu()
      })
    }

    if (backgroundColor && backgroundColor !== "") {
      // Toggle masthead background color when the top bar moves in and out of view
      const topBar = document.querySelector(topBarSelector)
      if (topBar) {
        const observer = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (!entry.isIntersecting) {
                removeNavBgColor(backgroundColor)
              } else {
                if (!navIsActive) {
                  addNavBgColor(backgroundColor)
                }
              }
            })
          },
          {
            rootMargin: "0px",
            threshold: 0,
          },
        )
        observer.observe(topBar)
      }

      // Toggle masthead background color on hover
      el.addEventListener("mouseover", function () {
        removeNavBgColor(backgroundColor)
      })

      el.addEventListener("mouseout", function () {
        if (window.scrollY <= topBarHeight) {
          if (!navIsActive) {
            addNavBgColor(backgroundColor)
          }
        }
      })
    }

    toggleMask(navItemEls)

    // Close menu on esc
    document.addEventListener("keyup", function (e) {
      if ("Escape" === e.key) {
        navItemEls.forEach(navItemEl => {
          let dropdownToggleEl: HTMLElement | null = navItemEl.querySelector(
            dropdownToggleSelector,
          )
          if (dropdownToggleEl) {
            if (
              dropdownToggleEl.classList.contains(dropdownToggleActiveClass)
            ) {
              dropdownToggleEl.focus()
            }
            hideDropdown(dropdownToggleEl)
          }
        })
      }
    })
  }

  function handleToggleNavItemClick(
    navItemEl: HTMLElement,
    event: MouseEvent,
    dropdownToggleEl: HTMLElement,
    backToMainEl: HTMLElement,
    dropdownEl: HTMLElement,
  ) {
    // Mouse clicks dropdown toggle target
    if (window.innerWidth >= 1200) {
      // Open Desktop menu
      if (event.target !== el.querySelector(dropdownSelector)) {
        if (dropdownToggleEl.classList.contains(dropdownToggleActiveClass)) {
          hideDropdown(dropdownToggleEl)
        } else {
          showDropdown(
            navItemEls,
            dropdownToggleEl,
            dropdownToggleActiveClass,
            maskActiveClass,
            dropdownToggleSelector,
          )
        }
      }
    } else {
      // Mobile menu actions
      let mobileTarget = event.target as HTMLElement
      // Check subMenu click
      if (
        // Check if target == subMenu BUTTON
        mobileTarget.classList.contains("masthead__sub-menu-toggle") ||
        // Check if target == BUTTON/SVG
        mobileTarget.parentElement?.classList.contains(
          "masthead__sub-menu-toggle",
        ) ||
        // Check if target == BUTTON/SVG/PATH
        mobileTarget.parentElement?.parentElement?.classList.contains(
          "masthead__sub-menu-toggle",
        )
      ) {
        subMenuItemEls.forEach(subMenuItem => {
          let subMenuToggleEl: HTMLDivElement | null =
            subMenuItem.querySelector(subMenuToggleSelector)
          if (subMenuToggleEl && subMenuToggleEl.contains(mobileTarget)) {
            if (subMenuItem.classList.contains(subMenuActiveClass)) {
              subMenuItem.classList.remove(subMenuActiveClass)
              subMenuToggleEl.setAttribute("aria-expanded", "false")
            } else {
              subMenuItem.classList.add(subMenuActiveClass)
              subMenuToggleEl.setAttribute("aria-expanded", "true")
            }
          }
        })
      }
      // Check clicked on BackToMain button
      else if (
        mobileTarget == backToMainEl ||
        backToMainEl.contains(mobileTarget)
      ) {
        backToMobileMainMenu(dropdownToggleEl, navItemEl, dropdownEl)
      } else {
        // Open mobile subMenu
        dropdownToggleEl.classList.add(dropdownToggleActiveClass)
        focusLock.on(dropdownEl)
      }
    }
  }

  function showDropdown(
    navItemEls: NodeListOf<Element> | null,
    dropdownToggleEl: HTMLElement,
    dropdownToggleActiveClass: string,
    maskActiveClass: string,
    dropdownToggleSelector: string,
  ) {
    if (navItemEls) {
      // Hide all open dropdowns (either from clicking or keyboard focus)
      navItemEls.forEach(navItemEl => {
        let dropdownToggleEl: HTMLElement | null = navItemEl.querySelector(
          dropdownToggleSelector,
        )
        hideDropdown(dropdownToggleEl)
      })
    }
    // Show selected dropdown
    dropdownToggleEl.classList.add(dropdownToggleActiveClass)
    dropdownToggleEl.setAttribute("aria-expanded", "true")
    el.classList.add(maskActiveClass)
    focusLock.on(el)
  }

  function hideDropdown(dropdownToggleEl: HTMLElement | null) {
    if (dropdownToggleEl) {
      dropdownToggleEl.classList.remove(dropdownToggleActiveClass)
      dropdownToggleEl.setAttribute("aria-expanded", "false")
      el.classList.remove(maskActiveClass)
      focusLock.off(el)
    }
  }

  function toggleMobileMenu() {
    if (navIsActive) {
      hideMobileMenu()
    } else {
      showMobileMenu()
    }
  }

  function backToMobileMainMenu(
    dropdownToggleEl: HTMLElement,
    navItemEl: HTMLElement,
    dropdownEl: HTMLElement,
  ) {
    dropdownToggleEl.classList.remove(dropdownToggleActiveClass)
    // Close open submenus
    const subMenuEls = navItemEl.querySelectorAll(subMenuSelector)
    subMenuEls.forEach(el => {
      if (el.classList.contains(subMenuActiveClass)) {
        el.classList.toggle(subMenuActiveClass)
      }
    })
    const scrollableMenuEl: HTMLElement | null = el.querySelector(menuSelector)
    if (scrollableMenuEl) {
      scrollableMenuEl.scrollTop = 0
    }
    focusLock.off(dropdownEl)
  }

  function showMobileMenu() {
    el.classList.add(mobileActiveClass)
    el.classList.add(maskActiveClass)
    if (!hamburgerToggleEl || !bodyEl || !menuEl) {
      return
    }
    hamburgerToggleEl.setAttribute("aria-expanded", "true")
    if (backgroundColor) {
      addNavBgColor(backgroundColor)
    }
    bodyEl.classList.add("mobile-menu-open")
    // Adjust the height of the mobile menu on tablet/mobile if the top bar is out of view
    let height: number
    if (window.innerWidth > 767 && window.innerWidth < 1200) {
      if (window.scrollY > topBarHeight) {
        height = window.innerHeight - 59
      } else {
        height = window.innerHeight - 97
      }
      menuEl.style.height = `${height}px`
    } else if (window.innerWidth <= 767) {
      height = window.innerHeight - 53
      menuEl.style.height = `${height}px`
    }
    focusLock.on(el)
    navIsActive = true
  }

  function hideMobileMenu() {
    el.classList.remove(mobileActiveClass)
    el.classList.remove(maskActiveClass)
    if (!hamburgerToggleEl || !bodyEl || !menuEl) {
      return
    }
    hamburgerToggleEl.setAttribute("aria-expanded", "false")
    if (backgroundColor && bodyEl.classList.contains("mobile-menu-open")) {
      removeNavBgColor(backgroundColor)
    }
    bodyEl.classList.remove("mobile-menu-open")
    if (window.innerWidth > 767) {
      menuEl.style.height = "auto"
    }
    focusLock.off(menuEl)
    // Close any a) active drawers and b) active submenus
    const activeMenuEls = el.querySelectorAll(dropdownToggleSelector)
    activeMenuEls.forEach(el => {
      if (el.classList.contains(dropdownToggleActiveClass)) {
        el.classList.remove(dropdownToggleActiveClass)
      }
    })
    const activeSubMenuEls = el.querySelectorAll(subMenuSelector)
    activeSubMenuEls.forEach(el => {
      let subMenuToggleEl = el.querySelector(subMenuToggleSelector)
      if (subMenuToggleEl) {
        subMenuToggleEl.setAttribute("aria-expanded", "false")
        if (el.classList.contains(subMenuActiveClass)) {
          el.classList.remove(subMenuActiveClass)
        }
      }
    })

    navIsActive = false
  }

  function addNavBgColor(backgroundColor: string) {
    el.classList.add(backgroundColor)
    el.classList.remove("bg-pale")
  }

  function removeNavBgColor(backgroundColor: string) {
    el.classList.remove(backgroundColor)
    el.classList.add("bg-pale")
  }

  function toggleMask(navItemEls: NodeListOf<HTMLElement>) {
    // Close menu on mask click
    const maskEl: HTMLElement | null = document.querySelector(".masthead-mask")
    if (!maskEl) {
      return
    }
    maskEl.addEventListener("click", function () {
      navItemEls.forEach(navItemEl => {
        let dropdownToggleEl: HTMLElement | null = navItemEl.querySelector(
          dropdownToggleSelector,
        )
        if (dropdownToggleEl) {
          if (dropdownToggleEl.classList.contains(dropdownToggleActiveClass)) {
            dropdownToggleEl.focus()
          }
          hideDropdown(dropdownToggleEl)
          if (document.activeElement) {
            ;(document.activeElement as HTMLElement).blur()
          }
        }
      })
    })
  }
}
